import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HardHat, Clock, FileText, AlertCircle, Calendar, ChevronRight, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCMS } from '../context/CMSContext';
import { cantieri, fatture, preventivi, promemoria, clienti } from '../utils/api';
import DataCard from '../components/shared/DataCard';
import Toast from '../components/shared/Toast';
import { useToast } from '../hooks/useToast';
import { STATI_ATTIVI, isCantiereInRitardo } from '../constants/cantieriStati';

const fmt = (n) => n == null ? '—' : Number(n).toLocaleString('it-IT');
const todayLabel = () => new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const tomorrowISO = () => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const addDaysISO = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtDataShort = (iso) => { if (!iso) return ''; const [, m, g] = iso.split('-'); return `${g}/${m}`; };
const daysSince = (iso) => { if (!iso) return 0; const d = new Date(iso); return Math.floor((Date.now() - d.getTime()) / 86400000); };
const nowH = () => String(new Date().getHours()).padStart(2, '0');
const nowM = () => String(new Date().getMinutes()).padStart(2, '0');

const STATO_COLOR = {
  nuovo: 'badge-nuovo', sopralluogo: 'badge-sopralluogo', in_corso: 'badge-in-corso',
  completato: 'badge-attivo', chiuso: 'badge-chiuso', sospeso: 'badge-attesa', annullato: 'badge-chiuso',
};
const STATO_LABEL = {
  nuovo: 'Nuovo', sopralluogo: 'Sopralluogo', in_corso: 'In corso',
  completato: 'Completato', chiuso: 'Chiuso', sospeso: 'Sospeso', annullato: 'Annullato',
};
const TIPO_PROM_COLOR = {
  sopralluogo: '#5DADE2', chiamata: 'var(--brand)', consegna: '#F2994A',
  preventivo: '#F2B94B', fattura: '#E66A6A', riunione: '#908D97', scadenza: '#E66A6A',
};
const TIPI_PROM = ['sopralluogo', 'chiamata', 'consegna', 'preventivo', 'fattura', 'riunione', 'scadenza'];

// Colori KPI come HEX letterali: feedono l'alpha-concat `${color}20` per il
// tint dell'icona — un token CSS (var(--brand)) produrrebbe "var(--brand)20"
// invalido (lezione Restyling Step 2). Allineati ai token: brand/danger/amber.
function KpiCard({ Icon, label, value, badgeText, badgeTone, color, onClick }) {
  const badgeColor = badgeTone === 'warn' ? 'var(--warn)' : 'var(--ds-danger)';
  return (
    <div className="kpi" onClick={onClick} style={{
      background: 'var(--chip)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)',
      padding: '16px 18px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)',
      cursor: onClick ? 'pointer' : 'default', flex: 1, minWidth: 0,
      transition: 'box-shadow .18s, border-color .18s, transform .18s',
    }}
      onMouseEnter={onClick ? (e) => {
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(31,42,40,.07),0 18px 42px rgba(31,42,40,.12)';
        e.currentTarget.style.borderColor = 'var(--border-2, var(--ds-border))';
        e.currentTarget.style.transform = 'translateY(-2px)';
      } : undefined}
      onMouseLeave={onClick ? (e) => {
        e.currentTarget.style.boxShadow = '0 6px 18px rgba(35,38,47,0.08)';
        e.currentTarget.style.borderColor = 'var(--ds-border)';
        e.currentTarget.style.transform = 'none';
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={17} strokeWidth={2} style={{ color }} />
        </div>
        {badgeText && (
          <span style={{ fontSize: 10, fontWeight: 600, color: '#fff', background: badgeColor, padding: '3px 9px', borderRadius: 99 }}>
            {badgeText}
          </span>
        )}
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 5 }}>{label}</div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const heroTag = useCMS('hero_tag', 'Il gestionale AI per l\'edilizia italiana');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [kpi, setKpi] = useState({
    cantieri: null, inRitardo: null, preventivi: null, scadute: null,
  });
  const [allPromemoria, setAllPromemoria] = useState([]);
  const [promTab, setPromTab] = useState('oggi');
  const [highlightProm, setHighlightProm] = useState(null);
  const promPanelRef = useRef(null);
  const [allCantieri, setAllCantieri] = useState([]);
  const [cantieriTab, setCantieriTab] = useState('tutti');
  const [loading, setLoading] = useState(true);

  // Breakpoint mobile (<768px) — stesso pattern di Layout.jsx. Solo layout:
  // le griglie a più colonne diventano colonna singola su mobile.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [scadenze, setScadenze] = useState([]);
  const [loadingScadenze, setLoadingScadenze] = useState(false);

  // "Cosa fare oggi" — priorità calcolate client-side da dati esistenti
  const [prevInviati, setPrevInviati] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loadingTodo, setLoadingTodo] = useState(true);
  const [showAllTodo, setShowAllTodo] = useState(false);

  // Promemoria form state
  const [showPromForm, setShowPromForm] = useState(false);
  const [promForm, setPromForm] = useState({ titolo: '', data: todayISO(), tipo: 'chiamata' });
  const { toasts, showToast, dismissToast } = useToast();
  // Righe con la chiusura in volo: disabilita il bottone e da' il feedback
  // di attesa senza bloccare il resto della lista.
  const [chiusuraInCorso, setChiusuraInCorso] = useState([]);
  const [promOraH, setPromOraH] = useState(nowH);
  const [promOraM, setPromOraM] = useState(nowM);
  const [promFormErr, setPromFormErr] = useState('');
  const [savingProm, setSavingProm] = useState(false);

  const reloadPromemoria = async () => {
    try {
      const [oggi, pros] = await Promise.all([promemoria.oggi(), promemoria.prossimi()]);
      const oggiList = oggi.data?.results || oggi.data || [];
      const prosList = pros.data?.results || pros.data || [];
      // Merge e deduplica per id
      const map = new Map();
      [...oggiList, ...prosList].forEach(p => map.set(p.id, p));
      setAllPromemoria(Array.from(map.values()));
    } catch {}
  };

  // Chiude un promemoria. L'endpoint esisteva gia' (`/promemoria/{id}/completa/`,
  // esposto in api.js dal primo giorno) e non lo chiamava nessuno: misurato il
  // 27/08 sul database vivo, 25 promemoria e ZERO mai completati. Gli arretrati
  // che la scheda "Oggi" adesso mostra esistono anche per questo.
  const completaPromemoria = async (p) => {
    if (chiusuraInCorso.includes(p.id)) return;
    setChiusuraInCorso(prev => [...prev, p.id]);
    try {
      await promemoria.completa(p.id);
      // Sparisce dalla lista: `oggi()` e `prossimi()` filtrano completato=False,
      // quindi lo stato locale deve fare la stessa cosa o la riga resterebbe
      // finche' non si ricarica la pagina.
      setAllPromemoria(prev => prev.filter(x => x.id !== p.id));
      showToast('Promemoria completato', 'success');
    } catch {
      showToast('Non sono riuscito a completare il promemoria. Riprova.', 'error');
    } finally {
      setChiusuraInCorso(prev => prev.filter(id => id !== p.id));
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, fatKpi, prevKpi, promOggi, promPros] = await Promise.allSettled([
          cantieri.list({ page_size: 200 }),
          fatture.kpi(),
          preventivi.kpi(),
          promemoria.oggi(),
          promemoria.prossimi(),
        ]);

        if (cRes.status === 'fulfilled') {
          const data = cRes.value.data;
          const items = Array.isArray(data.results || data) ? (data.results || data) : [];
          setAllCantieri(items);
          const today = todayISO();
          // Definizioni condivise con la lista cantieri (source unica).
          const attivi = items.filter(c => STATI_ATTIVI.includes(c.stato)).length;
          const inRitardo = items.filter(c => isCantiereInRitardo(c, today)).length;
          setKpi(k => ({ ...k, cantieri: attivi, inRitardo }));
        }
        if (fatKpi.status === 'fulfilled') {
          const d = fatKpi.value.data;
          setKpi(k => ({
            ...k,
            scadute: d.scadute_count ?? null,
          }));
        }
        if (prevKpi.status === 'fulfilled') {
          const d = prevKpi.value.data;
          setKpi(k => ({ ...k, preventivi: d.bozze ?? d.da_inviare ?? null }));
        }
        if (promOggi.status === 'fulfilled' || promPros.status === 'fulfilled') {
          const oggiList = promOggi.status === 'fulfilled' ? (promOggi.value.data?.results || promOggi.value.data || []) : [];
          const prosList = promPros.status === 'fulfilled' ? (promPros.value.data?.results || promPros.value.data || []) : [];
          const map = new Map();
          [...oggiList, ...prosList].forEach(p => map.set(p.id, p));
          setAllPromemoria(Array.from(map.values()));
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Fetch scadenze finanziarie
  useEffect(() => {
    const fetchScadenze = async () => {
      setLoadingScadenze(true);
      try {
        // "Da incassare" via INTENTO (?da_incassare=true): la lista degli stati
        // vive solo nel backend (kpi_helpers.STATI_DA_INCASSARE), il frontend non
        // ne tiene una copia. Ordine per scadenza server-side (ordering_fields);
        // cap a 5 client-side — niente page_size globale (FE-059).
        const res = await fatture.list({
          da_incassare: 'true',
          ordering: 'data_scadenza',
        });
        setScadenze((res.data.results || []).slice(0, 5));
      } catch {
        setScadenze([]);
      } finally {
        setLoadingScadenze(false);
      }
    };
    fetchScadenze();
  }, []);

  // Fetch dati per "Cosa fare oggi" (P1 preventivi inviati, P3 clienti prospect).
  // P2 (cantieri senza piano) usa allCantieri già caricato (campo ha_piano).
  useEffect(() => {
    const fetchTodo = async () => {
      setLoadingTodo(true);
      try {
        const [prevRes, prospRes] = await Promise.allSettled([
          preventivi.list({ stato: 'inviato', page_size: 100 }),
          clienti.list({ stato_lifecycle: 'prospect', page_size: 100 }),
        ]);
        if (prevRes.status === 'fulfilled') {
          const d = prevRes.value.data;
          setPrevInviati(d.results || d || []);
        }
        if (prospRes.status === 'fulfilled') {
          const d = prospRes.value.data;
          setProspects(d.results || d || []);
        }
      } finally {
        setLoadingTodo(false);
      }
    };
    fetchTodo();
  }, []);

  // Priorità "Cosa fare oggi": fino a 3 item DISTINTI. Prima un item per
  // categoria (varietà), poi backfill in ordine di priorità per riempire gli
  // slot liberi. Contatore degli eventuali item rimanenti.
  const _todoGroups = [
    // P1 — preventivi 'inviato' da >3gg (più vecchio primo)
    prevInviati
      .filter(p => p.stato === 'inviato' && daysSince(p.data_emissione) > 3)
      .sort((a, b) => daysSince(b.data_emissione) - daysSince(a.data_emissione))
      .map(p => ({
        key: `prev-${p.id}`, color: 'var(--warn)',
        text: `Sollecita ${p.cliente_nome || 'cliente'} — preventivo inviato da ${daysSince(p.data_emissione)} giorni`,
        cta: 'Rivedi e invia', onClick: () => navigate(`/preventivi?apri=${p.id}`),
      })),
    // P2 — cantieri 'in_corso' senza piano lavori
    allCantieri
      .filter(c => c.stato === 'in_corso' && c.ha_piano === false)
      .map(c => ({
        key: `cant-${c.id}`, color: 'var(--zone-blue)',
        text: `Pianifica i lavori: ${c.titolo}`,
        cta: 'Apri', onClick: () => navigate(`/cantieri/${c.id}`),
      })),
    // P3 — clienti 'prospect' in attesa di preventivo
    prospects.map(cl => ({
      key: `cli-${cl.id}`, color: 'var(--zone-amber)',
      text: `Ricontatta ${cl.nome} — prospect in attesa di preventivo`,
      cta: 'Apri', onClick: () => navigate(`/clienti?apri=${cl.id}`),
    })),
  ];
  const TODO_MAX = 3;
  // Lista completa ordinata: prima un item per categoria (varietà), poi tutti
  // gli extra in ordine di priorità. I primi TODO_MAX sono la vista compatta;
  // il toggle "Mostra tutte" espande l'intera lista inline.
  const todoAll = [];
  _todoGroups.forEach(g => { if (g.length) todoAll.push(g[0]); });
  for (const g of _todoGroups) {
    for (let i = 1; i < g.length; i++) todoAll.push(g[i]);
  }
  const todoItems = showAllTodo ? todoAll : todoAll.slice(0, TODO_MAX);
  const todoMore = todoAll.length - Math.min(todoAll.length, TODO_MAX);

  // Filtra promemoria per tab
  const filteredPromemoria = (() => {
    const today = todayISO();
    const tomorrow = tomorrowISO();
    const in7days = addDaysISO(7);
    switch (promTab) {
      // "oggi" include anche gli scaduti (da fare, non ancora completati):
      // altrimenti i promemoria arretrati sparirebbero da ogni scheda.
      case 'oggi': return allPromemoria.filter(p => p.data <= today);
      case 'domani': return allPromemoria.filter(p => p.data === tomorrow);
      case 'settimana': return allPromemoria.filter(p => p.data >= today && p.data <= in7days);
      default: return [];
    }
  })();

  // Deep-link da centro notifiche: /?promemoria={id} porta il pannello
  // Promemoria in vista, sceglie la tab giusta ed evidenzia l'item.
  useEffect(() => {
    const promId = new URLSearchParams(location.search).get('promemoria');
    if (!promId || allPromemoria.length === 0) return undefined;
    const p = allPromemoria.find((x) => String(x.id) === String(promId));
    if (p) {
      const today = todayISO(); const tomorrow = tomorrowISO(); const in7 = addDaysISO(7);
      if (p.data === tomorrow) setPromTab('domani');
      else if (p.data > today && p.data <= in7) setPromTab('settimana');
      else setPromTab('oggi');
    }
    setHighlightProm(promId);
    if (promPanelRef.current) promPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightProm(null), 2600);
    return () => clearTimeout(t);
  }, [location.search, allPromemoria]);

  const promEmptyMsg = {
    oggi: 'Nessun promemoria oggi',
    domani: 'Nessun promemoria domani',
    settimana: 'Nessun promemoria nei prossimi 7 giorni',
  };

  // Filtra cantieri per tab
  const filteredCantieri = (() => {
    const today = todayISO();
    switch (cantieriTab) {
      case 'attivi': return allCantieri.filter(c => STATI_ATTIVI.includes(c.stato));
      case 'sospesi': return allCantieri.filter(c => c.stato === 'sospeso');
      case 'ritardo': return allCantieri.filter(c => isCantiereInRitardo(c, today));
      case 'completati': return allCantieri.filter(c => c.stato === 'completato');
      default: return allCantieri;
    }
  })().slice(0, 5);

  const cantieriEmptyMsg = {
    tutti: 'Nessun cantiere trovato',
    attivi: 'Nessun cantiere attivo',
    sospesi: 'Nessun cantiere sospeso',
    ritardo: 'Nessun cantiere in ritardo',
    completati: 'Nessun cantiere completato',
  };

  const handleSaveProm = async () => {
    if (!promForm.titolo.trim()) { setPromFormErr('Inserisci un titolo'); return; }
    setSavingProm(true); setPromFormErr('');
    try {
      const payload = { ...promForm, ora: `${promOraH}:${promOraM}:00` };
      await promemoria.create(payload);
      setShowPromForm(false);
      setPromForm({ titolo: '', data: todayISO(), tipo: 'chiamata' });
      setPromOraH(nowH()); setPromOraM(nowM());
      await reloadPromemoria();
    } catch (e) {
      const errData = e?.response?.data;
      const msg = errData?.detail
        || (errData && typeof errData === 'object'
          ? Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(' · ')
          : null)
        || 'Errore nel salvataggio';
      setPromFormErr(msg);
    }
    setSavingProm(false);
  };

  const cancelPromForm = () => {
    setShowPromForm(false);
    setPromForm({ titolo: '', data: todayISO(), tipo: 'chiamata' });
    setPromOraH(nowH()); setPromOraM(nowM());
    setPromFormErr('');
  };

  const pillStyle = (active) => ({
    fontSize: 11, fontWeight: 500, padding: '10px 16px', borderRadius: 99, border: 'none', cursor: 'pointer',
    background: active ? 'rgba(46,160,132,0.12)' : 'transparent',
    color: active ? 'var(--brand)' : 'var(--ds-muted)',
    transition: 'all 0.15s',
  });

  const promTabStyle = (active) => ({
    fontSize: 11, fontWeight: 500, padding: '10px 16px', borderRadius: 99, border: 'none', cursor: 'pointer',
    background: active ? 'rgba(46,160,132,0.12)' : 'transparent',
    color: active ? 'var(--brand)' : 'var(--ds-muted)',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: 0, lineHeight: 1.2 }}>
            Benvenuto, {user?.nome}!
          </h1>
          <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: '5px 0 0' }}>
            {heroTag} · {todayLabel()}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--ds-muted)', background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', padding: '7px 12px', boxShadow: '0 2px 10px rgba(35,38,47,0.06)' }}>
          <Calendar size={13} strokeWidth={2} />
          {new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      </div>

      {/* ── Banner WhatsApp ── */}
      {!bannerDismissed && !user?.whatsapp_phone && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 12,
          padding: '12px 16px',
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>📱</span>
          <span style={{ fontSize: 12, color: '#92400E', flex: 1 }}>
            Collega WhatsApp per usare i comandi vocali e ricevere notifiche
          </span>
          <a
            href="/impostazioni#whatsapp"
            style={{ fontSize: 12, fontWeight: 500, color: '#92400E', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Configura ora →
          </a>
          <button
            onClick={() => setBannerDismissed(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontSize: 16, padding: '0 0 0 8px', lineHeight: 1, flexShrink: 0, opacity: 0.7 }}
          >✕</button>
        </div>
      )}

      {/* ── Cosa fare oggi ── */}
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: '18px 20px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Cosa fare oggi</span>
        </div>

        {(loading || loadingTodo) ? (
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', padding: '6px 0' }}>Caricamento priorità...</div>
        ) : todoItems.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 0' }}>
            <span style={{ fontSize: 22 }}>🎉</span>
            <p style={{ fontSize: 13, color: 'var(--ds-muted)', margin: 0 }}>Tutto in ordine oggi</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {todoItems.map((item, i) => (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 'var(--radius-base)',
                background: 'var(--chip)', border: '1px solid var(--ds-border)',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: `color-mix(in srgb, ${item.color} 16%, transparent)`,
                  color: item.color, fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{item.text}</div>
                <button onClick={item.onClick} style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                  background: 'transparent', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)',
                  color: 'var(--brand)', fontSize: 12, fontWeight: 600, padding: '8px 14px', cursor: 'pointer',
                }}>
                  {item.cta}
                  <ChevronRight size={13} strokeWidth={2.5} />
                </button>
              </div>
            ))}
            {todoMore > 0 && (
              <button
                onClick={() => setShowAllTodo(v => !v)}
                style={{
                  alignSelf: 'flex-start', marginTop: 2, padding: '4px 2px',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--brand)', fontSize: 12, fontWeight: 600,
                }}
              >
                {showAllTodo ? 'Mostra meno' : `Mostra tutte (+${todoMore})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Urgenze del giorno — 4 KPI ── */}
      {/* Mobile: 2 KPI per riga (2x2) — a 4 colonne su 375px la 4ª card esce dal viewport. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
        <KpiCard Icon={HardHat}     label="Cantieri attivi"        value={loading ? '...' : fmt(kpi.cantieri)}    color="#2EA084" onClick={() => navigate('/cantieri?vista=attivi')} />
        <KpiCard Icon={Clock}       label="Cantieri in ritardo"    value={loading ? '...' : fmt(kpi.inRitardo)}   color="#E5533D" badgeText={kpi.inRitardo > 0 ? 'Urgente' : undefined}     badgeTone="danger" onClick={() => {
          // A1 — >1 → lista filtrata (?vista=ritardo); =1 → dritti nel cantiere.
          const inRit = allCantieri.filter(c => isCantiereInRitardo(c, todayISO()));
          if (inRit.length === 1) navigate(`/cantieri/${inRit[0].id}`);
          else navigate('/cantieri?vista=ritardo');
        }} />
        <KpiCard Icon={FileText}    label="Preventivi da inviare"  value={loading ? '...' : fmt(kpi.preventivi)}  color="#F0A23C" badgeText={kpi.preventivi > 0 ? 'Da fare' : undefined}     badgeTone="warn"   onClick={() => navigate('/preventivi?stato=bozza')} />
        <KpiCard Icon={AlertCircle} label="Fatture scadute"        value={loading ? '...' : fmt(kpi.scadute)}     color="#E5533D" badgeText={kpi.scadute > 0 ? 'Attenzione' : undefined}     badgeTone="danger" onClick={() => navigate('/fatture?stato=scaduta')} />
      </div>

      {/* ── Promemoria + Pannello Scadenze/Attività ── */}
      {/* Mobile: colonna singola. A 2 colonne su <768px le due card si
          allineano in altezza (grid align-items:stretch): la più corta si
          allunga creando uno spazio bianco prima della sezione Cantieri. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>

        {/* Colonna sinistra — Promemoria */}
        <div ref={promPanelRef} style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: '18px 20px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          {/* Mobile: header a capo — titolo+tab+bottone non stanno in 343px e
              allargherebbero la card oltre il viewport (taglio orizzontale). */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: 14, gap: isMobile ? 10 : 0 }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Promemoria</span>
              <div style={{ display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {[
                  { key: 'oggi', label: 'Oggi' },
                  { key: 'domani', label: 'Domani' },
                  { key: 'settimana', label: 'Prossimi 7gg' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setPromTab(tab.key)} style={promTabStyle(promTab === tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setShowPromForm(v => !v)}
              style={{ fontSize: 11, fontWeight: 500, color: 'var(--brand)', background: 'transparent', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', padding: '10px 16px', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--chip)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              + Aggiungi
            </button>
          </div>

          {/* Inline form */}
          {showPromForm && (
            <div style={{ marginBottom: 14, padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)' }}>
              <input
                type="text"
                placeholder="Cosa devi fare?"
                value={promForm.titolo}
                onChange={e => setPromForm(f => ({ ...f, titolo: e.target.value }))}
                style={{ width: '100%', borderRadius: 8, border: '1px solid var(--ds-border)', padding: '8px 10px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8, outline: 'none', background: 'var(--ds-bg)', color: 'var(--ink)' }}
              />
              {/* Mobile: data e ora impilate (niente taglio su schermi stretti).
                  Orario = input time nativo (picker mobile) al posto dei due
                  micro-input number, difficili da editare su touch. */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginBottom: 8 }}>
                <input
                  type="date"
                  value={promForm.data}
                  onChange={e => setPromForm(f => ({ ...f, data: e.target.value }))}
                  style={{ flex: 1, borderRadius: 8, border: '1px solid var(--ds-border)', padding: '7px 10px', fontSize: 12, outline: 'none', background: 'var(--ds-bg)', color: 'var(--ink)', boxSizing: 'border-box' }}
                />
                <input
                  type="time"
                  value={`${promOraH}:${promOraM}`}
                  onChange={e => {
                    const [h, m] = (e.target.value || '00:00').split(':');
                    setPromOraH((h || '00').padStart(2, '0'));
                    setPromOraM((m || '00').padStart(2, '0'));
                  }}
                  style={{ flex: 1, borderRadius: 8, border: '1px solid var(--ds-border)', padding: '7px 10px', fontSize: 12, outline: 'none', background: 'var(--ds-bg)', color: 'var(--ink)', boxSizing: 'border-box' }}
                />
              </div>
              <select
                value={promForm.tipo}
                onChange={e => setPromForm(f => ({ ...f, tipo: e.target.value }))}
                style={{ width: '100%', borderRadius: 8, border: '1px solid var(--ds-border)', padding: '7px 10px', fontSize: 12, boxSizing: 'border-box', marginBottom: 8, outline: 'none', background: 'var(--ds-bg)', color: 'var(--ink)' }}
              >
                {TIPI_PROM.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
              {promFormErr && <div style={{ fontSize: 11, color: 'var(--ds-danger)', marginBottom: 6 }}>{promFormErr}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleSaveProm}
                  disabled={savingProm}
                  style={{ flex: 1, borderRadius: 8, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 12, fontWeight: 500, padding: '7px 0', cursor: 'pointer' }}
                >
                  {savingProm ? '...' : 'Salva'}
                </button>
                <button
                  onClick={cancelPromForm}
                  style={{ flex: 1, borderRadius: 8, border: '1px solid var(--ds-border)', background: 'none', color: 'var(--ds-muted)', fontSize: 12, padding: '7px 0', cursor: 'pointer' }}
                >
                  Annulla
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--ds-muted)', padding: '12px 0' }}>Caricamento...</div>
          ) : filteredPromemoria.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '18px 0', gap: 8 }}>
              <Calendar size={28} strokeWidth={1.5} style={{ color: 'var(--ds-muted)' }} />
              <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: 0 }}>{promEmptyMsg[promTab]}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filteredPromemoria.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', margin: '0 -8px', borderBottom: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', background: String(highlightProm) === String(p.id) ? 'color-mix(in srgb, var(--brand) 12%, var(--ds-bg))' : 'transparent', transition: 'background .3s' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: TIPO_PROM_COLOR[p.tipo] || 'var(--brand)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titolo}</div>
                    <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>
                      {/* La scheda "Oggi" adesso porta anche gli ARRETRATI: senza la
                          data non si distingue un promemoria di stamattina da uno di
                          settantanove giorni fa, e la lista mentirebbe sul proprio nome. */}
                      {promTab === 'oggi' && p.data && p.data < todayISO() && (
                        <span style={{ fontWeight: 500, color: 'var(--danger, var(--ink))' }}>
                          {fmtDataShort(p.data)} · in ritardo ·{' '}
                        </span>
                      )}
                      {promTab !== 'oggi' && p.data && <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{fmtDataShort(p.data)} · </span>}
                      {p.ora || ''}{p.ora ? ' · ' : ''}{p.tipo}
                    </div>
                  </div>
                  {p.completato ? (
                    <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 500 }}>✓</span>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); completaPromemoria(p); }}
                      disabled={chiusuraInCorso.includes(p.id)}
                      title="Segna come completato"
                      aria-label={`Completa: ${p.titolo}`}
                      style={{
                        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                        border: '1px solid var(--ds-border)', background: 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: chiusuraInCorso.includes(p.id) ? 'default' : 'pointer',
                        opacity: chiusuraInCorso.includes(p.id) ? 0.45 : 1,
                        color: 'var(--brand)', padding: 0,
                      }}
                    >
                      <Check size={14} strokeWidth={2} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Colonna destra — Scadenze finanziarie */}
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: '18px 20px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Scadenze finanziarie</span>
          </div>

          {/* Contenuto Scadenze */}
          <>
              {loadingScadenze ? (
                <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Caricamento...</div>
              ) : scadenze.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--ds-muted)', textAlign: 'center', padding: '20px 0' }}>
                  Nessuna scadenza imminente
                </div>
              ) : (
                <>
                  {scadenze.map(f => {
                    // Differenza di giorni CIVILI esatta: parso la scadenza come
                    // mezzanotte LOCALE (new Date('YYYY-MM-DD') sarebbe UTC) e azzero
                    // l'ora di oggi → niente due fusi mescolati nella stessa sottrazione.
                    const hasScad = !!f.data_scadenza;
                    let gg = null, dataFmt = '';
                    if (hasScad) {
                      const [y, m, d] = f.data_scadenza.split('-').map(Number);
                      const scad = new Date(y, m - 1, d);
                      const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
                      gg = Math.round((scad - oggi) / 86400000);
                      dataFmt = scad.toLocaleDateString('it-IT');
                    }
                    // Colori/soglie invariati; +tono muted per "senza scadenza".
                    const urgenza = !hasScad ? 'none' : gg <= 2 ? 'urgente' : gg <= 4 ? 'warn' : 'ok';
                    const tone = { urgente: 'var(--ds-danger)', warn: 'var(--warn)', ok: 'var(--ok)', none: 'var(--ds-muted)' }[urgenza];
                    // Badge stato-aware (riga1/riga2) + sottotitolo: distingue
                    // "scade oggi" da "scaduta da N gg" (prima Math.max(0,gg) le fondeva).
                    let badgeMain, badgeSub, sottotitolo;
                    if (!hasScad)      { badgeMain = '—';          badgeSub = '';      sottotitolo = 'Senza scadenza'; }
                    else if (gg > 0)   { badgeMain = gg;           badgeSub = 'gg';    sottotitolo = `Scade il ${dataFmt}`; }
                    else if (gg === 0) { badgeMain = 'oggi';       badgeSub = '';      sottotitolo = 'Scade oggi'; }
                    else               { badgeMain = Math.abs(gg); badgeSub = 'gg fa'; sottotitolo = `Scaduta il ${dataFmt}`; }
                    return (
                      <div key={f.id}
                        onClick={() => navigate('/fatture')}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--chip)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 4px', borderRadius: 6,
                          borderBottom: '1px solid var(--ds-border)',
                          cursor: 'pointer', transition: 'background 0.1s',
                        }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 8,
                          background: `color-mix(in srgb, ${tone} 14%, transparent)`,
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <span style={{ fontSize: badgeMain === 'oggi' ? 11 : 14, fontWeight: 600, color: tone, lineHeight: 1 }}>
                            {badgeMain}
                          </span>
                          {badgeSub && <span style={{ fontSize: 9, color: tone }}>{badgeSub}</span>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {f.numero} · {f.cliente_nome || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 1 }}>
                            {sottotitolo}
                          </div>
                        </div>
                        <div style={{
                          fontSize: 13, fontWeight: 600,
                          color: tone,
                          flexShrink: 0,
                        }}>
                          € {Number(f.totale).toLocaleString('it-IT')}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', paddingTop: 10, marginTop: 4,
                    borderTop: '1px solid var(--ds-border)',
                  }}>
                    <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>Totale in scadenza</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>
                      € {scadenze.reduce((acc, f) => acc + Number(f.totale), 0).toLocaleString('it-IT')}
                    </span>
                  </div>
                </>
              )}
            </>
        </div>
      </div>

      {/* ── Griglia: Cantieri recenti + Azioni rapide ── */}
      {/* Mobile: colonna singola (Cantieri sopra, Azioni rapide sotto). */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 14 }}>

        {/* Cantieri recenti con filtro */}
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--ds-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Cantieri</span>
              <div style={{ display: 'flex', gap: 2 }}>
                {[
                  { key: 'tutti', label: 'Tutti' },
                  { key: 'attivi', label: 'Attivi' },
                  { key: 'sospesi', label: 'Sospesi' },
                  { key: 'ritardo', label: 'In ritardo' },
                  { key: 'completati', label: 'Completati' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setCantieriTab(tab.key)} style={pillStyle(cantieriTab === tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => navigate('/cantieri')} style={{ fontSize: 12, color: 'var(--brand)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
              Vai ai Cantieri
              <ChevronRight size={14} strokeWidth={2.5} />
            </button>
          </div>

          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--ds-muted)', textAlign: 'center', padding: '28px 0' }}>Caricamento...</div>
          ) : filteredCantieri.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              <HardHat size={32} strokeWidth={1.5} style={{ color: 'var(--ds-muted)', margin: '0 auto 10px', display: 'block' }} />
              <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: 0 }}>{cantieriEmptyMsg[cantieriTab]}</p>
            </div>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px' }}>
              {filteredCantieri.map(c => (
                <DataCard
                  key={c.id}
                  title={c.titolo}
                  subtitle={c.cliente_nome || c.cliente?.nome || null}
                  onClick={() => navigate(`/cantieri/${c.id}`)}
                  badge={<span className={`badge ${STATO_COLOR[c.stato] || 'badge-nuovo'}`}>{STATO_LABEL[c.stato] || c.stato}</span>}
                  meta={[
                    { label: 'Priorità', value: <span style={{ textTransform: 'capitalize', fontWeight: 500, color: c.priorita === 'alta' ? 'var(--ds-danger)' : c.priorita === 'media' ? 'var(--warn)' : 'var(--ds-muted)' }}>{c.priorita || '—'}</span> },
                  ]}
                />
              ))}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'var(--chip)' }}>
                  {['Cantiere', 'Stato', 'Cliente', 'Priorità', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '9px 16px', fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500, borderBottom: '1px solid var(--ds-border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCantieri.map(c => (
                  <tr key={c.id} className="table-row" onClick={() => navigate(`/cantieri/${c.id}`)}>
                    <td style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--ink)', borderBottom: '1px solid var(--ds-border)' }}>{c.titolo}</td>
                    <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--ds-border)' }}>
                      <span className={`badge ${STATO_COLOR[c.stato] || 'badge-nuovo'}`}>{STATO_LABEL[c.stato] || c.stato}</span>
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 12, color: 'var(--ds-muted)', borderBottom: '1px solid var(--ds-border)' }}>{c.cliente_nome || c.cliente?.nome || '—'}</td>
                    <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--ds-border)' }}>
                      <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'capitalize', color: c.priorita === 'alta' ? 'var(--ds-danger)' : c.priorita === 'media' ? 'var(--warn)' : 'var(--ds-muted)' }}>
                        {c.priorita || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--ds-border)', textAlign: 'right' }}>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); navigate(`/cantieri/${c.id}`); }}>
                        Dettaglio →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Azioni rapide */}
        <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', padding: '18px 20px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 14 }}>Azioni rapide</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Nuovo\nCantiere',    to: '/cantieri/nuovo',   icon: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8L6 7h12l-2-4z"/><line x1="12" y1="12" x2="12" y2="18" strokeLinecap="round"/><line x1="9" y1="15" x2="15" y2="15" strokeLinecap="round"/></> },
              { label: 'Crea\nPreventivo',  to: '/preventivi/nuovo', icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12" strokeLinecap="round"/><line x1="9" y1="15" x2="15" y2="15" strokeLinecap="round"/></> },
              { label: 'Emetti\nFattura',   to: '/fatture/nuovo',    icon: <><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></> },
              { label: 'Nuovo\nCliente',    to: '/clienti/nuovo',    icon: <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14" strokeLinecap="round"/><line x1="22" y1="11" x2="16" y2="11" strokeLinecap="round"/></> },
              { label: 'Nuovo\nEvento',     to: '/calendario/nuovo', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><line x1="12" y1="14" x2="12" y2="18" strokeLinecap="round"/><line x1="10" y1="16" x2="14" y2="16" strokeLinecap="round"/></> },
              { label: 'Nuovo\nDocumento',  to: '/documenti',        icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="13" strokeLinecap="round"/><line x1="9" y1="15" x2="15" y2="15" strokeLinecap="round"/><line x1="9" y1="12" x2="13" y2="12" strokeLinecap="round"/></> },
            ].map(({ label, to, icon }) => (
              <button key={to} onClick={() => navigate(to)} className="tile gradient-brand"
                style={{ borderRadius: 'var(--radius-base)', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '14px 6px', cursor: 'pointer' }}>
                <svg style={{ width: 20, height: 20 }} fill="none" stroke="var(--ink)" strokeWidth="1.5" viewBox="0 0 24 24">{icon}</svg>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 600, color: 'var(--ink)', textAlign: 'center', lineHeight: 1.3, whiteSpace: 'pre-line' }}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <Toast toasts={toasts} dismissToast={dismissToast} />
    </div>
  );
}
